/* All intellectual property rights in the software, code and associated materials ("Software") belong to Leo Learning Limited and its licensors. All rights in the Software are expressly reserved by and to Leo Learning Limited and its licensors. Without limiting the generality of the foregoing, users of the Software are not permitted to (and agree not to) reproduce, copy or otherwise duplicate, and not to distribute, lend or otherwise transfer the Software or any part of it without the express written permission of Leo Learning Limited. Each user also undertakes not to use the Software in any way that would compromise the confidential and proprietary nature of the Software. Each user undertakes to use the Software only in accordance with the terms of the respective user license and not to resell, reproduce, sell, modify the Software in any way and acknowledges that it has no permission to so do. */ // eslint-disable-line max-len
/* global gettext, interpolate */
import React from 'react'; // eslint-disable-line no-unused-vars
import PropTypes from 'prop-types';
import ImmutablePropTypes from 'react-immutable-proptypes';
import { List } from 'immutable';


const PAGE_LINK_TEXT = {
  FIRST: gettext('First'),
  PREVIOUS: gettext('Previous'),
  NEXT: gettext('Next'),
  LAST: gettext('Last'),
};

export class ListPagination extends React.PureComponent {
  static getPageListStartPageNumber(activePage, numberOfPages, numberOfPagesToRender) {
    const idealStartPage = Math.max(activePage - Math.floor(numberOfPagesToRender / 2), 1);
    return (idealStartPage + numberOfPagesToRender) - 1 <= numberOfPages ?
      idealStartPage :
      Math.max(1, (numberOfPages - numberOfPagesToRender) + 1);
  }

  loadPage(page, activeLink) {
    if (activeLink) {
      this.props.loadPage(page);
    }
  }

  renderPaginationLink(page, name, isDisabled = false, isActive = false) {
    const key = name;
    const anchorClassName = isDisabled ? 'disabled' : '';
    let listItemClassName = isActive ? 'current' : '';
    if (name === PAGE_LINK_TEXT.NEXT || name === PAGE_LINK_TEXT.LAST) {
      listItemClassName += ' pagination-next';
    } else if (name === PAGE_LINK_TEXT.PREVIOUS || name === PAGE_LINK_TEXT.FIRST) {
      listItemClassName += ' pagination-previous';
    }
    const displayName = name in PAGE_LINK_TEXT ? PAGE_LINK_TEXT[name] : name;
    return (
      <li key={key} className={listItemClassName}>
        { isActive ? (
          <span
            key={key}
            className="show-for-sr"
          >
            {displayName}
          </span>
        ) : (
          <a
            role="link"
            tabIndex={0}
            className={anchorClassName}
            onClick={() => this.loadPage(page, !isActive && !isDisabled)}
          >
            {displayName}
          </a>
        )
        }
      </li>
    );
  }

  renderPageList(activePage, numberOfPages) {
    const pageListStartPageNumber =
      ListPagination.getPageListStartPageNumber(activePage, numberOfPages, 5);
    return List([0, 1, 2, 3, 4]).map((idx) => {
      const pageNumber = pageListStartPageNumber + idx;
      return pageNumber <= numberOfPages &&
        this.renderPaginationLink(pageNumber, pageNumber, false, pageNumber === activePage);
    });
  }

  render() {
    const { collection, pageSize } = this.props;
    const { page, count, items, hasMore } = collection.toObject();

    const loadedItemsCount = items ? items.count() : 0;
    const firstItemNumber = loadedItemsCount === 0 ? 0 : ((page - 1) * pageSize) + 1;
    const lastItemNumber = Math.max(
      (firstItemNumber + Math.min(pageSize, loadedItemsCount)) - 1,
      0,
    );

    const numberOfPages = Math.ceil(count / pageSize);
    const isFirstPage = page === 1;
    const isLastPage = !hasMore;

    return (
      <div className="list-pagination mt-1 clearfix">
        <div className="grid-x">
          <div className="cell medium-6 pagination-count">
            {interpolate(
              gettext('Showing %(firstItemNumber)s to %(lastItemNumber)s of %(count)s entries.'),
              { firstItemNumber, lastItemNumber, count },
              true,
            )}
          </div>
          <div className="cell medium-6">
            <ul className="pagination float-right" role="navigation" aria-label="Pagination">
              {this.renderPaginationLink(1, PAGE_LINK_TEXT.FIRST, isFirstPage)}
              {this.renderPaginationLink(page - 1, PAGE_LINK_TEXT.PREVIOUS, isFirstPage)}
              {this.renderPageList(page, numberOfPages)}
              {this.renderPaginationLink(page + 1, PAGE_LINK_TEXT.NEXT, isLastPage)}
              {this.renderPaginationLink(numberOfPages, PAGE_LINK_TEXT.LAST, isLastPage)}
            </ul>
          </div>
        </div>
      </div>
    );
  }
}

ListPagination.propTypes = {
  collection: ImmutablePropTypes.map.isRequired,
  loadPage: PropTypes.func.isRequired,
  pageSize: PropTypes.number.isRequired,
};

export default ListPagination;
